import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Printer, Leaf } from 'lucide-react';
import Logo from '../components/site/Logo';

const Protocol = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Executive Medical Wellness Program — Elevate Medical Clinics';
  }, []);

  const onDownload = () => window.print();

  return (
    <div className="protocol-root bg-[#EEE8DD] min-h-screen py-8 print:p-0 print:bg-white">
      {/* Print styles */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          .no-print { display: none !important; }
          html, body { background: white !important; }
          .protocol-root { padding: 0 !important; }
          .protocol-page {
            width: 210mm !important;
            min-height: 297mm !important;
            box-shadow: none !important;
            margin: 0 !important;
            border-radius: 0 !important;
            page-break-after: always;
          }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      {/* Toolbar (screen only) */}
      <div className="no-print max-w-[210mm] mx-auto mb-6 flex items-center justify-between px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#1F3A2E] hover:text-[#B8935A]">
          <ArrowLeft size={14} /> Back to site
        </Link>
        <button
          onClick={onDownload}
          className="inline-flex items-center gap-2 bg-[#1F3A2E] text-[#F7F3EC] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#142822] transition-colors"
        >
          <Download size={16} /> Download PDF
        </button>
      </div>

      {/* A4 document */}
      <article
        className="protocol-page mx-auto bg-[#F7F3EC] shadow-[0_20px_60px_-20px_rgba(31,58,46,0.35)] rounded-md overflow-hidden"
        style={{ width: '210mm', minHeight: '297mm' }}
      >
        {/* Green header band */}
        <header className="bg-[#1F3A2E] text-[#F7F3EC] relative overflow-hidden">
          {/* DNA imagery — right-side bleed */}
          <div className="absolute inset-y-0 right-0 w-[42%] pointer-events-none">
            <img
              src="https://images.unsplash.com/photo-1633167606207-d840b5070fc2?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt=""
              className="h-full w-full object-cover"
              style={{ mixBlendMode: 'screen', opacity: 0.55 }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1F3A2E] via-transparent to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1F3A2E]/60 via-transparent to-[#1F3A2E]/30" />
          </div>

          <div className="relative px-10 pt-8 pb-7">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Logo size={44} dark />
                <div className="leading-tight">
                  <div className="font-serif text-[19px]" style={{ letterSpacing: '-0.01em' }}>Elevate</div>
                  <div className="text-[9px] uppercase" style={{ letterSpacing: '0.3em', color: 'rgba(247,243,236,0.7)' }}>
                    Medical Clinics
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-[0.28em] text-[#D4B48A]">Clinical Protocol · v1.0</div>
                <div className="text-[9px] text-[rgba(247,243,236,0.7)] mt-1">Effective: Jan 2026</div>
              </div>
            </div>

            <div className="mt-6 max-w-[62%]">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4B48A]">Program Overview</div>
              <h1 className="font-serif text-[36px] leading-[1.05] mt-2 tracking-tight">
                Executive Medical <em className="italic">Wellness</em> Program
              </h1>
              <div className="mt-2 text-[13px] text-[rgba(247,243,236,0.9)]">
                A scientific, evidence-based protocol for measurable improvements in metabolic health, performance and longevity.
              </div>
            </div>
          </div>
        </header>

        {/* Body */}
        <section className="px-10 py-6 text-[#1E2A24] text-[10.5px] leading-[1.5]">
          {/* Row 1: Purpose + Framework */}
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <SectionTitle>1. Program Purpose</SectionTitle>
              <p>
                The Executive Medical Wellness Program is Elevate Medical Clinics' flagship physician-led pathway — an
                integrated protocol combining advanced diagnostics with lifestyle-medicine principles. Its aim is to
                identify root-cause drivers of disease, optimise cardiometabolic and hormonal function, and support
                long-term vitality, cognitive performance and healthspan.
              </p>
            </div>
            <div>
              <SectionTitle>2. Clinical Framework</SectionTitle>
              <ul className="space-y-1">
                <PillarLI title="Prevention-first" desc="early detection over late intervention" />
                <PillarLI title="Precision" desc="care built around your unique biology" />
                <PillarLI title="Vitality" desc="function, energy, resilience & longevity" />
                <PillarLI title="Root-cause" desc="underlying drivers, not just symptoms" />
              </ul>
            </div>
          </div>

          {/* Row 2: 6-phase protocol */}
          <div className="mt-5">
            <SectionTitle>3. The Six-Phase Protocol</SectionTitle>
            <div className="grid grid-cols-6 gap-2">
              {phases.map((p, i) => (
                <div key={p.title} className="border border-[rgba(31,58,46,0.15)] rounded-md p-2.5 bg-white">
                  <div className="text-[8.5px] uppercase tracking-[0.18em] text-[#B8935A]">Phase 0{i + 1}</div>
                  <div className="font-serif text-[11.5px] text-[#1F3A2E] mt-1 leading-tight">{p.title}</div>
                  <div className="text-[9px] text-[#4a544e] mt-1 leading-snug">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Row 3: Data collected */}
          <div className="mt-5 grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="h-9 w-9 rounded-full overflow-hidden border border-[rgba(31,58,46,0.15)] shrink-0"
                  style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1486825586573-7131f7991bdd?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }}
                  aria-hidden
                />
                <SectionTitle noMargin>4. Data Points Collected</SectionTitle>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                {dataPoints.map((d) => (
                  <div key={d.k} className="flex items-baseline gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#B8935A] mt-1.5 shrink-0" />
                    <div>
                      <span className="font-medium text-[#1F3A2E]">{d.k}: </span>
                      <span className="text-[#4a544e]">{d.v}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <SectionTitle>5. Deliverables</SectionTitle>
              <ul className="space-y-1">
                {deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#1F3A2E] mt-1.5 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Row 4: Care team + Outcomes */}
          <div className="mt-5 grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <SectionTitle>6. Multidisciplinary Care Team</SectionTitle>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                {team.map((t) => (
                  <div key={t.name} className="flex items-baseline gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#1F3A2E] mt-1.5 shrink-0" />
                    <div>
                      <span className="font-medium text-[#1F3A2E]">{t.name} </span>
                      <span className="text-[#4a544e]">— {t.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="h-9 w-9 rounded-full overflow-hidden border border-[rgba(31,58,46,0.15)] shrink-0 grayscale"
                  style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1542190891-2093d38760f2?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center 25%' }}
                  aria-hidden
                />
                <SectionTitle noMargin>7. Outcomes & Governance</SectionTitle>
              </div>
              <p className="text-[#4a544e]">
                Progress is tracked using objective clinical markers, validated symptom indices and patient-reported
                outcome measures at baseline, 3, 6 and 12 months. Every plan undergoes physician review; care is
                adjusted based on response, adherence and evolving goals.
              </p>
            </div>
          </div>

          {/* Callout */}
          <div className="mt-5 rounded-md bg-[#1F3A2E] text-[#F7F3EC] px-5 py-3.5 flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-[#B8935A] text-[#1F3A2E] grid place-items-center shrink-0">
              <Leaf size={16} strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <div className="font-serif text-[13px] leading-tight">
                <em className="italic">True healthcare starts before illness begins. Prevention is the new cure.</em>
              </div>
              <div className="text-[8.5px] uppercase tracking-[0.24em] text-[#D4B48A] mt-1">
                Dr Anthony Chaffee · Principal Doctor
              </div>
            </div>
          </div>
        </section>

        {/* Footer strip */}
        <footer className="mt-1 px-10 py-4 border-t border-[rgba(31,58,46,0.15)] flex items-center justify-between text-[9px] text-[#4a544e]">
          <div>Elevate Medical Clinics · Unit 6, 689 Beaufort Street, Mount Lawley WA 6050</div>
          <div>Page 1 of 2 · +61 432 809 498 · reception@elevatemedicalclinics.com.au</div>
        </footer>
      </article>

      {/* =============== PAGE 2 =============== */}
      <article
        className="protocol-page mx-auto bg-[#F7F3EC] shadow-[0_20px_60px_-20px_rgba(31,58,46,0.35)] rounded-md overflow-hidden mt-10 print:mt-0"
        style={{ width: '210mm', minHeight: '297mm' }}
      >
        {/* Cream header band (lighter, matches page 1 layout) */}
        <header className="bg-[#1F3A2E] text-[#F7F3EC] relative overflow-hidden">
          <div className="absolute inset-y-0 right-0 w-[38%] pointer-events-none">
            <img
              src="https://images.unsplash.com/photo-1486825586573-7131f7991bdd?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt=""
              className="h-full w-full object-cover"
              style={{ opacity: 0.35 }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1F3A2E] via-[#1F3A2E]/60 to-transparent" />
          </div>
          <div className="relative px-10 pt-7 pb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Logo size={40} dark />
                <div className="leading-tight">
                  <div className="font-serif text-[18px]" style={{ letterSpacing: '-0.01em' }}>Elevate</div>
                  <div className="text-[9px] uppercase" style={{ letterSpacing: '0.3em', color: 'rgba(247,243,236,0.7)' }}>Medical Clinics</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-[0.28em] text-[#D4B48A]">Detailed Protocol · Page 2</div>
                <div className="text-[9px] text-[rgba(247,243,236,0.7)] mt-1">Executive Medical Wellness Program</div>
              </div>
            </div>
            <div className="mt-4 max-w-[62%]">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4B48A]">Program Detail</div>
              <h2 className="font-serif text-[28px] leading-[1.05] mt-1 tracking-tight">
                Diagnostic panel, interventions <em className="italic">and</em> monitoring cadence.
              </h2>
            </div>
          </div>
        </header>

        <section className="px-10 py-6 text-[#1E2A24] text-[10.5px] leading-[1.5]">
          {/* A. Diagnostic Panel */}
          <div>
            <SectionTitle>A. Diagnostic Panel — measured at baseline</SectionTitle>
            <div className="grid grid-cols-3 gap-3">
              {[
                { k: 'Cardiometabolic', v: 'Fasting glucose & insulin, HOMA-IR, HbA1c, oral glucose tolerance (where indicated), lipid panel with ApoB and Lp(a), hs-CRP, uric acid, homocysteine.' },
                { k: 'Hormonal', v: 'TSH, fT3, fT4, thyroid antibodies, morning cortisol, DHEA-S, total & free testosterone, SHBG, oestradiol, LH/FSH, progesterone (cycle-timed).' },
                { k: 'Nutritional', v: 'Vitamin D (25-OH), B12, folate, iron studies, ferritin, magnesium, zinc, RBC folate.' },
                { k: 'Liver, kidney & haem', v: 'LFTs, U&E, eGFR, full blood count, iron overload screen where indicated.' },
                { k: 'Body composition', v: 'Weight, waist-to-hip ratio, BMI; DEXA scan on referral for visceral fat, lean mass and bone density.' },
                { k: 'Cardiovascular risk', v: 'Blood pressure, resting heart rate, cardiovascular risk stratification; ECG and coronary calcium score on referral where clinically indicated.' },
              ].map((d) => (
                <div key={d.k} className="rounded-md border border-[rgba(31,58,46,0.12)] bg-white p-3">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#B8935A]">{d.k}</div>
                  <div className="mt-1.5 text-[10px] text-[#1E2A24] leading-snug">{d.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 text-[9px] text-[#4a544e] italic">
              All investigations are ordered by the treating GP based on individual history and clinical indication. Advanced imaging (DEXA, coronary calcium, carotid ultrasound) is arranged via referral to accredited providers.
            </div>
          </div>

          {/* B. Interventions matrix */}
          <div className="mt-5">
            <SectionTitle>B. Personalised Intervention Matrix</SectionTitle>
            <div className="grid grid-cols-5 gap-2">
              {[
                { k: 'Medical', v: 'Diagnosis review, targeted pharmacotherapy where clinically indicated, chronic-disease management plans.' },
                { k: 'Nutrition', v: 'Dietitian-led plan aligned to biomarkers, goals and preferences. Continuous glucose monitoring where useful.' },
                { k: 'Movement', v: 'Resistance training, aerobic base building and VO₂-focused sessions guided by baseline fitness.' },
                { k: 'Sleep', v: 'Sleep hygiene assessment, circadian alignment strategies, screening for obstructive sleep apnoea.' },
                { k: 'Stress', v: 'Structured recovery practices, HRV-informed guidance via wearables, referral to psychology where indicated.' },
              ].map((d) => (
                <div key={d.k} className="rounded-md bg-[#EEE8DD] border border-[rgba(31,58,46,0.1)] p-3">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#B8935A]">{d.k}</div>
                  <div className="mt-1.5 text-[10px] text-[#1E2A24] leading-snug">{d.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* C. Executive considerations */}
          <div className="mt-5 grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <SectionTitle>C. Executive-focused Considerations</SectionTitle>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {[
                  ['Cognitive clarity', 'Stabilising blood glucose and addressing sleep deficits to reduce afternoon energy troughs.'],
                  ['Energy management', 'Iron, B12, thyroid and cortisol review to identify treatable drivers of fatigue.'],
                  ['Travel resilience', 'Practical, portable protocols for jet-lag, sleep and nutrition during travel.'],
                  ['Cardiovascular protection', 'Advanced lipid assessment (ApoB, Lp(a)) and structured risk-factor management.'],
                  ['Body composition', 'Preserving lean mass and reducing visceral fat via evidence-based nutrition and training.'],
                  ['Longevity focus', 'Prevention and early detection with a strong emphasis on modifiable risk factors.'],
                ].map(([k, v]) => (
                  <li key={k} className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#1F3A2E] mt-1.5 shrink-0" />
                    <div>
                      <span className="font-medium text-[#1F3A2E]">{k}: </span>
                      <span className="text-[#4a544e]">{v}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <SectionTitle>D. Delivered In-Clinic vs. Referred</SectionTitle>
              <div className="text-[10px] leading-snug">
                <div className="font-medium text-[#1F3A2E]">Delivered at Elevate</div>
                <div className="text-[#4a544e]">GP consultations, dietitian consultations, nursing services, pathology collection, plan writing and review.</div>
                <div className="font-medium text-[#1F3A2E] mt-2">Arranged by referral</div>
                <div className="text-[#4a544e]">DEXA scans, coronary calcium & carotid imaging, sleep studies, exercise physiology, psychology, specialist care.</div>
              </div>
            </div>
          </div>

          {/* E. Monitoring cadence */}
          <div className="mt-5">
            <SectionTitle>E. Monitoring Cadence</SectionTitle>
            <div className="grid grid-cols-5 gap-2">
              {[
                { k: 'Baseline', v: 'Comprehensive intake, full diagnostic panel, imaging where indicated, written plan.' },
                { k: '6 weeks', v: 'Plan review, symptom check, adherence, early adjustments.' },
                { k: '3 months', v: 'Repeat targeted bloods, refine nutrition and movement, dietitian review.' },
                { k: '6 months', v: 'Full clinical review, updated markers, structured re-plan.' },
                { k: '12 months', v: 'Comprehensive re-assessment against baseline; long-term strategy set.' },
              ].map((d) => (
                <div key={d.k} className="rounded-md border border-[rgba(31,58,46,0.15)] bg-white p-2.5">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#B8935A]">{d.k}</div>
                  <div className="mt-1 text-[10px] text-[#1E2A24] leading-snug">{d.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* F. Governance */}
          <div className="mt-5 grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <SectionTitle>F. Governance, Safety & Evidence Base</SectionTitle>
              <p className="text-[#4a544e]">
                All care is delivered by AHPRA-registered practitioners under RACGP standards. Investigations, interpretations and treatment decisions are made on clinical indication. The program integrates evidence from cardiology, endocrinology, nutrition and lifestyle-medicine literature, and does not make claims of guaranteed outcomes. Individual response varies and depends on adherence, biology and health history.
              </p>
            </div>
            <div>
              <SectionTitle>G. Privacy</SectionTitle>
              <p className="text-[#4a544e]">
                Your medical data is handled in accordance with the Privacy Act 1988 (Cth) and Australian Privacy Principles. Records are stored in a secure practice-management system and shared only with treating clinicians or with your written consent.
              </p>
            </div>
          </div>
        </section>

        <footer className="mt-1 px-10 py-4 border-t border-[rgba(31,58,46,0.15)] flex items-center justify-between text-[9px] text-[#4a544e]">
          <div>Elevate Medical Clinics · Unit 6, 689 Beaufort Street, Mount Lawley WA 6050</div>
          <div>Page 2 of 2 · +61 432 809 498 · reception@elevatemedicalclinics.com.au</div>
        </footer>
      </article>

      {/* Screen-only hint */}
      <div className="no-print max-w-[210mm] mx-auto mt-6 px-6 flex items-center gap-2 text-xs text-[#4a544e]">
        <Printer size={14} />
        Tip: Click "Download PDF" and choose "Save as PDF" in the print dialog. Set margins to <span className="font-medium">"None"</span> for the best result.
      </div>
    </div>
  );
};

const SectionTitle = ({ children, noMargin = false }) => (
  <div className={`text-[9px] uppercase tracking-[0.28em] text-[#B8935A] font-medium ${noMargin ? '' : 'mb-2'}`}>{children}</div>
);

const PillarLI = ({ title, desc }) => (
  <li className="flex items-baseline gap-2">
    <span className="w-1 h-1 rounded-full bg-[#B8935A] mt-1.5 shrink-0" />
    <div>
      <span className="font-medium text-[#1F3A2E]">{title}</span>
      <span className="text-[#4a544e]"> — {desc}</span>
    </div>
  </li>
);

const phases = [
  { title: 'Comprehensive Intake', desc: 'History, symptoms, lifestyle, family history, goals' },
  { title: 'Advanced Investigations', desc: 'Pathology, functional testing, cardiometabolic markers' },
  { title: 'Root-Cause Analysis', desc: 'Clinical synthesis using functional-medicine frameworks' },
  { title: 'Personalised Written Plan', desc: 'Nutrition, movement, sleep, stress, pharmacology (if indicated)' },
  { title: 'Integrated Care Team', desc: 'GP, dietitian & nurse working collaboratively on outcomes' },
  { title: 'Ongoing Monitoring', desc: 'Scheduled reviews, plan iteration, outcome tracking' },
];

const dataPoints = [
  { k: 'Cardiometabolic', v: 'fasting insulin, HOMA-IR, HbA1c, lipids, ApoB, hs-CRP, uric acid, homocysteine' },
  { k: 'Hormonal', v: 'thyroid panel, sex hormones, cortisol, DHEA-S' },
  { k: 'Nutritional', v: 'vitamin D, B12, folate, iron studies, magnesium, zinc' },
  { k: 'Body composition', v: 'weight, WHR, muscle mass, visceral adiposity' },
  { k: 'Cardiovascular', v: 'BP, resting HR, cardiac risk stratification' },
  { k: 'Lifestyle indices', v: 'sleep, activity, nutrition, stress & symptom scales' },
];

const deliverables = [
  'Physician-led assessment',
  'Written, personalised protocol',
  'Coordinated dietitian & nurse plan',
  'Structured review schedule',
  'Adaptive adjustments over time',
];

const team = [
  { name: 'Dr Anthony Chaffee', role: 'Principal Doctor · MBBS RCSI' },
  { name: 'Dr Sam Beh', role: 'GP · Lifestyle Medicine, FRACGP' },
  { name: 'Sanjana Kaur', role: 'Accredited Clinical Dietitian' },
  { name: 'Ms Nuri Evrita', role: 'Registered Nurse · Aesthetic' },
  { name: 'Dr Zaheer Mohamed', role: 'DBA · Founder & CEO' },
  { name: 'Frances Zampogna', role: 'Lead Medical Receptionist' },
];

export default Protocol;
